import { LoginCredentials } from "@/pages/Login";
import { type Layout } from "@/hooks/useLayout";
import axios from "axios";
import {
   onReqFulfilled,
   onReqRejected,
   onResFulfilled,
   onResRejected,
} from "./helpers";
import { ApiResponse } from "@/types/api.types";
import { ISubject } from "@/types/subject.types";
import { ITerms } from "@/types/terms.types";
import { Class } from "@/types/class.types";
import { ILoginResponse } from "@/types/user.types";
import { AssignmentSubmission } from "@/types/assignments.types";
import { returnStringByLayout } from "@/lib/utils";
import { ILesson, ILessonByWeek } from "@/types/lesson.types";

const api = axios.create({
   baseURL: process.env.REACT_APP_OLD_API_BASEURL,
   headers: {
      "Content-Type": "multipart/form-data",
   },
});

api.interceptors.request.use(onReqFulfilled, onReqRejected);
api.interceptors.response.use(onResFulfilled, onResRejected);

export default api;

export const listSchools = () => api.get("", { params: { action: "list_schools" } })

export const getSchoolDetails = (params) => api.get("", { params: { action: "get_school_details", ...params } })

// Auth
export const loginFn =
   (layout: Layout = "student") =>
      (data: LoginCredentials): Promise<ILoginResponse> =>
         api.post(``, {
            action: returnStringByLayout(layout, { student: "signin", teacher: "signin_staff", admin: "signin_admin" }),
            ...data
         });

export const changePasswordFn = (layout: Layout = "student") => (data): Promise<ILoginResponse> =>
   api.post(`/`, {
      action: returnStringByLayout(layout, { student: "update_student_password", teacher: "update_staff_password", admin: "update_school_admin_password" }),
      ...data
   });

export const verifySchoolCubeToken = (data): Promise<ILoginResponse<{ type: "admin" | "teacher" | "student" }>> => api.post("/", { action: "authenticate_token", ...data })

//? Class Levels
export const getClassLevels = (): Promise<ApiResponse<string[]>> =>
   api.get("/", { params: { action: "get_class_levels" } });

export const updateClassLevel = (data): Promise<ApiResponse> => api.post(`/`, { action: "update_class_level", ...data });

//? Subjects
export const getSubjects = (params): Promise<ApiResponse<ISubject[]>> =>
   api.get("", { params: { action: "get_all_subjects", ...params } });

export const updateSubject = (data): Promise<ApiResponse> => api.post(`/`, { action: "update_subject", ...data });

// ?Classes
export const getClasses = (params): Promise<ApiResponse<Class[]>> =>
   api.get("", { params: { action: "get_all_arms", ...params } });

export const updateClass = (data): Promise<ApiResponse> => api.post(`/`, { action: "update_class", ...data });

// ? Terms
export const getTerms = (): Promise<ApiResponse<ITerms[]>> => api.get("", { params: { action: "get_terms" } });

// ? Lessons

export const createLesson = ({ lesson_id, ...data }): Promise<ApiResponse> => api.post("/", { action: "create_lesson", ...data });

export const getLessons = () => (params): Promise<ApiResponse<ILessonByWeek[]>> => api.get(``, { params: { ...params, action: "get_lessons" } });

export const getLessonsList = () => (params): Promise<ApiResponse<ILesson[]>> => api.get(``, { params: { ...params, action: "get_lessons_list" } });

export const getLessonsCount = () => (params) => api.get(``, { params: { ...params, action: "get_lessons_count" } });

export const updateLesson = (data): Promise<ApiResponse> => api.post(`/`, { action: "update_lesson", ...data });

export const approveLesson = ({ id }): Promise<ApiResponse> => api.post(``, { action: "update_lesson_status", lessonID: id, status: "approved" });

export const rejectLesson = ({ id }): Promise<ApiResponse> => api.post(``, { action: "update_lesson_status", lessonID: id, status: "rejected" });

// ? Assignment
export const createAssignment = (data): Promise<ApiResponse> =>
   api.post("/", { action: "create_assignment", ...data });

export const getAssignmentDetails = (id): Promise<ApiResponse> => api.get(`/`, { params: { action: "get_lesson_assignments", lesson_id: id } });

export const getAssignmentSubmissions = (assignment_id): Promise<ApiResponse<AssignmentSubmission[]>> =>
   api.get(`/`, { params: { action: "get_assignment_submissions", assignment_id } });

export const gradeAssignmentSubmission = (data): Promise<ApiResponse> =>
   api.post(`/`, { action: "grade_assignment_submission", ...data });

export const submitAssignment = (data): Promise<ApiResponse> =>
   api.post(`/`, { action: "submit_assignment", ...data });

export const uploadLessonVideo = ({ school_code, uploadName, media }) => axios
   .post(
      "https://services.classcube.online/api/v1/media/upload",
      {
         uploadCollection: school_code,
         uploadName,
         media,
      },
      {
         headers: {
            "Content-Type": "multipart/form-data",
            "x-api-key": process.env.REACT_APP_STREAMING_APIKEY,
         },
      }
   )

// ? Announcements
export const createAnnouncement = (data): Promise<ApiResponse> =>
   api.post("/", { action: "register_lesson_announcement", ...data });
